/**
 * Action atoms for agent status and report stream events.
 *
 * Separated from NexaStore.ts so the store stays as pure state definitions
 * (atoms, interfaces, defaults) with no business logic imports.
 *
 * Consumers dispatch these via useSetAtom(), which returns a referentially
 * stable function — no dependency array churn.
 */

import { atom } from 'jotai';
import type { QueryClient } from '@tanstack/react-query';
import { logger } from '@src/utils/misc/logger';
import { AGENT_STATUS_PHASE_ORDER, AgentStatusStreamEvent, ReportStreamEvent } from './utils';
import {
  agentStatusAtom,
  AgentStatusState,
  DEFAULT_AGENT_STATUS,
  reportStreamAtom,
  ReportStreamState,
  DEFAULT_REPORT_STREAM,
  isReportPanelOpenAtom,
  isReportGeneratingAtom,
  activeReportVersionAtom,
  getSessionId,
} from './NexaStore';

// ============================================================================
// Agent Status action atoms
// ============================================================================

const MIN_AGENT_STATUS_VISIBLE_MS = 800;
// Module-level timer — there is only ever one agent status stream active at a time
let _agentStatusTimer: ReturnType<typeof setTimeout> | null = null;

export const clearAgentStatusAtom = atom(null, (_get, set) => {
  if (_agentStatusTimer) {
    clearTimeout(_agentStatusTimer);
    _agentStatusTimer = null;
  }
  set(agentStatusAtom, DEFAULT_AGENT_STATUS);
});

export const handleAgentResponseRenderedAtom = atom(null, (get, set) => {
  const current = get(agentStatusAtom);
  if (current.terminalPendingClear) {
    if (_agentStatusTimer) {
      clearTimeout(_agentStatusTimer);
      _agentStatusTimer = null;
    }
    set(agentStatusAtom, DEFAULT_AGENT_STATUS);
  } else {
    set(agentStatusAtom, { ...current, responseRendered: true });
  }
});

export const handleAgentStatusEventAtom = atom(
  null,
  (get, set, payload: AgentStatusStreamEvent) => {
    if (!payload || typeof payload !== 'object') return;

    const streamId = payload.stream_id || null;
    if (!streamId) return;

    let current = get(agentStatusAtom);
    const isNewStream = current.streamId !== streamId;

    if (isNewStream) {
      if (_agentStatusTimer) {
        clearTimeout(_agentStatusTimer);
        _agentStatusTimer = null;
      }
      current = { ...DEFAULT_AGENT_STATUS, streamId };
      set(agentStatusAtom, current);
    }

    const seq = typeof payload.seq === 'number' ? payload.seq : current.seq + 1;
    if (!isNewStream && seq <= current.seq) return;

    if (payload.phase === 'error') {
      if (_agentStatusTimer) {
        clearTimeout(_agentStatusTimer);
        _agentStatusTimer = null;
      }
      set(agentStatusAtom, DEFAULT_AGENT_STATUS);
      return;
    }

    if (payload.phase === 'completed') {
      if (current.responseRendered) {
        if (_agentStatusTimer) {
          clearTimeout(_agentStatusTimer);
          _agentStatusTimer = null;
        }
        set(agentStatusAtom, DEFAULT_AGENT_STATUS);
        return;
      }
      set(agentStatusAtom, { ...current, seq, terminalPendingClear: true });
      return;
    }

    current = { ...current, terminalPendingClear: false };

    // Heartbeats keep sequence freshness but should not churn banner text.
    if (payload.is_heartbeat) {
      set(agentStatusAtom, { ...current, seq });
      return;
    }

    const currentPhase = current.phase;
    const currentMessage = current.message;
    const displayedAtMs = current.displayedAtMs || 0;
    const incomingPhase = payload.phase || currentPhase;
    const incomingMessage = payload.message || currentMessage || '⚙️ Working...';

    if (!incomingPhase) {
      set(agentStatusAtom, { ...current, seq });
      return;
    }

    const applyDisplay = (base: AgentStatusState): AgentStatusState => ({
      ...base,
      phase: incomingPhase,
      message: incomingMessage,
      seq,
      displayedAtMs: Date.now(),
      pendingTransition: null,
    });

    if (!currentPhase) {
      set(agentStatusAtom, applyDisplay(current));
      return;
    }

    const currentRank = AGENT_STATUS_PHASE_ORDER[currentPhase] || 0;
    const incomingRank = AGENT_STATUS_PHASE_ORDER[incomingPhase] || currentRank;

    // Enforce monotonic forward-only phase progression in UI.
    if (incomingRank < currentRank) {
      set(agentStatusAtom, { ...current, seq });
      return;
    }

    if (incomingRank === currentRank) {
      if (incomingMessage !== currentMessage) {
        set(agentStatusAtom, applyDisplay(current));
      } else {
        set(agentStatusAtom, { ...current, seq });
      }
      return;
    }

    // Higher rank: throttle transition to ensure minimum display time.
    const elapsed = Date.now() - displayedAtMs;
    const waitMs = Math.max(0, MIN_AGENT_STATUS_VISIBLE_MS - elapsed);

    if (_agentStatusTimer) {
      clearTimeout(_agentStatusTimer);
      _agentStatusTimer = null;
    }
    set(agentStatusAtom, { ...current, pendingTransition: null });

    if (waitMs === 0) {
      set(agentStatusAtom, applyDisplay(current));
      return;
    }

    set(agentStatusAtom, {
      ...current,
      pendingTransition: { phase: incomingPhase, message: incomingMessage, seq },
    });

    _agentStatusTimer = setTimeout(() => {
      const latest = get(agentStatusAtom);
      if (latest.streamId !== streamId || seq < latest.seq) return;
      set(agentStatusAtom, applyDisplay(latest));
      _agentStatusTimer = null;
    }, waitMs);
  },
);

// ============================================================================
// Report Stream action atoms
// ============================================================================

export const clearReportStreamAtom = atom(null, (_get, set) => {
  set(reportStreamAtom, DEFAULT_REPORT_STREAM);
});

export const handleReportStreamEventAtom = atom(
  null,
  (get, set, payload: { event: ReportStreamEvent; queryClient: QueryClient }) => {
    const { event, queryClient } = payload;
    if (!event || typeof event !== 'object') return;

    const streamId = event.stream_id || null;
    if (!streamId) return;

    const current = get(reportStreamAtom);
    const hasActiveStream = Boolean(current.streamId);
    const isNewStream = current.streamId !== streamId;
    const currentIsTerminal =
      !current.phase || current.phase === 'done' || current.phase === 'error';

    if (isNewStream && hasActiveStream && (!currentIsTerminal || event.phase !== 'start')) {
      return;
    }

    let state = current;
    if (isNewStream) {
      state = { ...DEFAULT_REPORT_STREAM, streamId };
      set(reportStreamAtom, state);
      set(isReportPanelOpenAtom, true);
    }

    const seq = typeof event.seq === 'number' ? event.seq : state.seq + 1;
    if (!isNewStream && seq <= state.seq) return;

    const updated: ReportStreamState = { ...state, seq };
    if (event.phase) updated.phase = event.phase;
    if (event.message) updated.message = event.message;
    if (typeof event.version === 'number') updated.version = event.version;
    if (event.delta_html) updated.html = state.html + event.delta_html;

    set(reportStreamAtom, updated);

    if (event.phase === 'done') {
      const versionFromStream = typeof event.version === 'number' ? event.version : null;
      const sessionId = getSessionId();

      // Optimistically start persisted-version fetch as soon as stream says "done".
      if (versionFromStream) {
        set(activeReportVersionAtom, versionFromStream);
        if (sessionId) {
          queryClient.invalidateQueries({ queryKey: ['nexa-report-versions', sessionId] });
        }
      }

      // Keep report generation UX active until authoritative tool state arrives.
      set(reportStreamAtom, {
        ...get(reportStreamAtom),
        message: 'Finalizing and syncing saved report...',
      });
      return;
    }

    if (event.phase === 'error') {
      set(isReportGeneratingAtom, false);
    }
  },
);

export const handleReportToolCallAtom = atom(null, (_get, set) => {
  set(isReportPanelOpenAtom, true);
  set(isReportGeneratingAtom, true);
  set(reportStreamAtom, DEFAULT_REPORT_STREAM);
});

export const handleReportToolStateAtom = atom(
  null,
  (get, set, payload: { stateValue: any; queryClient: QueryClient }) => {
    const { stateValue, queryClient } = payload;
    try {
      if (stateValue.status === 'success' && stateValue.version) {
        const sessionId = getSessionId();
        logger.log(
          '[ToolHandlers] Report generated, version:',
          stateValue.version,
          'sessionId:',
          sessionId,
        );

        // Use setTimeout to ensure the query is fully mounted and React Query has processed it
        setTimeout(async () => {
          try {
            logger.log('[ToolHandlers] Invalidating query for sessionId:', sessionId);
            await queryClient.invalidateQueries({
              queryKey: ['nexa-report-versions', sessionId],
            });
            set(activeReportVersionAtom, stateValue.version);
            set(reportStreamAtom, {
              ...get(reportStreamAtom),
              version: stateValue.version,
              phase: 'done',
              message: 'Loading saved report version...',
            });
            set(isReportGeneratingAtom, false);
          } catch (e) {
            logger.error('[ToolHandlers] Error in setTimeout refetch:', e);
            set(isReportGeneratingAtom, false);
            set(reportStreamAtom, DEFAULT_REPORT_STREAM);
          }
        }, 10);
      } else {
        set(isReportGeneratingAtom, false);
        set(reportStreamAtom, DEFAULT_REPORT_STREAM);
      }
    } catch (e) {
      logger.error('[ToolHandlers] Error processing generate_static_report state:', e);
      set(isReportGeneratingAtom, false);
      set(reportStreamAtom, DEFAULT_REPORT_STREAM);
    }
  },
);
